import {createAsyncThunk} from '@reduxjs/toolkit';
import {AxiosResponse} from 'axios';
import {ACTION_TYPE} from '../actions-types';
import {getErrorResponse} from '@/utils/helpers';
import {
  IAdminAllEventsResponse,
  IAdminAllOrganizationsResponse,
  IAdminAllTransactionsResponse,
  IAdminInfo,
  IAdminLogin2FactorRequest,
  IAdminLoginRequest,
  IAdminLoginResponse,
  IAdminOrganizationResponse,
  IAdminPercentage,
  IAdminStatisticResponse,
  IEventsListRequest,
  IOrganizationsListRequest,
  ITransactionsListRequest,
} from '@/utils/types/superAdmin.interface';
import {adminStorage, SuperAdminService} from '@/utils/services';
import {STORAGE_KEY} from '@/utils/enums';

// export const superAdminLogin = createAsyncThunk(
//   ACTION_TYPE.SUPER_ADMIN_LOGIN,
//   async (reqBody: IAdminLoginRequest, {rejectWithValue}) => {
//     try {
//       const adminResponse: AxiosResponse<IAdminLoginResponse> = await SuperAdminService.login(reqBody);

//       adminStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, adminResponse.data.tokens.jwtBearerToken);
//       adminStorage.set(STORAGE_KEY.REFRESH_TOKEN, adminResponse.data.tokens.refreshToken);
//       adminStorage.set(STORAGE_KEY.SUPER_ADMIN_ID, adminResponse.data.admin.id);

//       return adminResponse.data;
//     } catch (error) {
//       return rejectWithValue(getErrorResponse(error));
//     }
//   }
// );

export const getSuperAdminInfo = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_GET_INFO,
  async (id: number, {rejectWithValue}) => {
    try {
      const adminResponse: AxiosResponse<IAdminInfo> = await SuperAdminService.getInfo(id);

      return adminResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const superAdminLogout = createAsyncThunk(ACTION_TYPE.SUPER_ADMIN_LOGOUT, () => adminStorage.clear());

export const updateSuperAdminProfile = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_UPDATE_PROFILE,
  async (reqBody: IAdminInfo, {rejectWithValue}) => {
    try {
      await SuperAdminService.changeProfile(reqBody);
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const getAllOrganizations = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_GET_ALL_ORGANIZATIONS,
  async (reqBody: IOrganizationsListRequest, {rejectWithValue}) => {
    try {
      const organizationsResponse: AxiosResponse<IAdminAllOrganizationsResponse> =
        await SuperAdminService.getAllOrganizations(reqBody);
      return organizationsResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const getAdminOrganizationById = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_GET_ORGANIZATION,
  async (id: number, {rejectWithValue}) => {
    try {
      const organizationsResponse: AxiosResponse<IAdminOrganizationResponse> =
        await SuperAdminService.getOrganizationById(id);
      return organizationsResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const getAllOrganizationsTransactions = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_GET_ALL_ORGANIZATION_TRANSACTIONS,
  async (reqBody: ITransactionsListRequest, {rejectWithValue}) => {
    try {
      const organizationsResponse: AxiosResponse<IAdminAllTransactionsResponse> =
        await SuperAdminService.getAllOrganizationsTransactions(reqBody);
      return organizationsResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const getAllOrganizationsEvents = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_GET_ALL_ORGANIZATION_EVENTS,
  async (reqBody: IEventsListRequest, {rejectWithValue}) => {
    try {
      const organizationsResponse: AxiosResponse<IAdminAllEventsResponse> = await SuperAdminService.getAllEvents(
        reqBody
      );
      return organizationsResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const getAdminStatistic = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_GET_STATISTICS,
  async (statisticsTerm: string, {rejectWithValue}) => {
    try {
      const organizationsResponse: AxiosResponse<IAdminStatisticResponse> = await SuperAdminService.getAdminStatistic(
        statisticsTerm
      );
      return organizationsResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const getPlatformFee = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_GET_PLATFORM_FEE,
  async (_, {rejectWithValue}) => {
    try {
      const organizationsResponse: AxiosResponse<IAdminPercentage> = await SuperAdminService.getDefaultPlatformFee();
      return organizationsResponse.data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const superAdminLogin = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_LOGIN,
  async (reqBody: IAdminLoginRequest, {rejectWithValue}) => {
    try {
      const {data}: AxiosResponse<IAdminLoginResponse> = await SuperAdminService.login(reqBody);

      if (data.requiresTwoFactor) return data;

      adminStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, data.tokens.jwtBearerToken);
      adminStorage.set(STORAGE_KEY.REFRESH_TOKEN, data.tokens.refreshToken);
      adminStorage.set(STORAGE_KEY.SUPER_ADMIN_ID, data.admin.id);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);

export const superAdminLogin2Factor = createAsyncThunk(
  ACTION_TYPE.SUPER_ADMIN_LOGIN_2FACTOR,
  async (reqBody: IAdminLogin2FactorRequest, {rejectWithValue}) => {
    try {
      const {data}: AxiosResponse<IAdminLoginResponse> = await SuperAdminService.login2Factor(reqBody);

      if (data.requiresTwoFactor) return rejectWithValue('Two-factor verification failed');

      adminStorage.set(STORAGE_KEY.JWT_BEARER_TOKEN, data.tokens.jwtBearerToken);
      adminStorage.set(STORAGE_KEY.REFRESH_TOKEN, data.tokens.refreshToken);
      adminStorage.set(STORAGE_KEY.SUPER_ADMIN_ID, data.admin.id);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorResponse(error));
    }
  }
);
